import React, { useState, useEffect, useMemo } from 'react';
import { CategoryItem } from '../types';
import { TreeNode } from './TreeNode';
import { AddItemInput } from './AddItemInput';
import { AICardForm } from './AICardForm';
import { CardDetail } from './CardDetail';
import { CategoryView } from './CategoryView';
import { CategoryPracticePhrasesSession } from './CategoryPracticePhrasesSession';
import { CategoryPracticeSession } from './CategoryPracticeSession';
import { 
  ChevronRight,
  ChevronDown,
  Folder,
  Plus,
  Hash,
  Tag,
  FileText,
  Check,
  X,
  Sparkles,
  FolderInput,
  UserPlus,
  Zap,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuItem, createEditAction, createDeleteAction } from './DropdownMenu';
import { MoveToCollectionModal } from './MoveToCollectionModal';
import { GenerateStoryModal } from './GenerateStoryModal';
import { AddTextModal } from './AddTextModal';
import { ArrangeWithAIModal } from './ArrangeWithAIModal';
import { PracticePickerModal } from './PracticePickerModal';
import { ContextualPracticeModal } from './ContextualPracticeModal';
import { AssignFolderModal } from './AssignFolderModal';
import { AIFolderGenerator } from './AIFolderGenerator';
import { handleConditionalDelete } from '../utils/deleteUtils';
import { SHOW_PRACTICE_PHRASES_IN_MENU } from '../constants/uiVisibility';
import { flattenTree } from '../utils/treeUtils';
import { apiClient } from '../services/api';
import { useInstance } from '../hooks/useInstance';
import { useAuth } from '../hooks/useAuth';

interface CategoryNodeProps {
  category: CategoryItem;
  onToggle: (id: number) => void;
  onRename: (id: number, newName: string) => void;
  onDelete: (id: number) => void;
  onAddChild: (parentId: number, type: 'folder' | 'card', name: string) => void;
  onMove: (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => Promise<void>;
  onCategoryUpdate: () => void;
  onCategoryRefresh: (categoryId: number, expandFolderId?: number) => void;
  categories?: CategoryItem[];
  isInstanceOwner?: boolean;
  /** When this matches `category.id`, category view opens once; parent clears via `onCategoryViewRequestHandled`. */
  openCategoryViewRequestId?: number | null;
  onCategoryViewRequestHandled?: () => void;
  isSelectionMode?: boolean;
  selectedCardIds?: Set<number>;
  onSelectCard?: (id: number, selected: boolean) => void;
}

export const CategoryNode: React.FC<CategoryNodeProps> = ({
  category,
  onToggle,
  onRename,
  onDelete,
  onAddChild,
  onMove,
  onCategoryUpdate,
  onCategoryRefresh,
  categories = [],
  isInstanceOwner: propIsInstanceOwner = false,
  openCategoryViewRequestId = null,
  onCategoryViewRequestHandled,
  isSelectionMode = false,
  selectedCardIds = new Set(),
  onSelectCard,
}) => {
  const { selectedInstanceId, instancesWithRoles } = useInstance();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(category.name);
  const [showAddInput, setShowAddInput] = useState(false);
  const [addType, setAddType] = useState<'folder' | 'card'>('folder');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAICardForm, setShowAICardForm] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [showCategoryView, setShowCategoryView] = useState(false);
  const [showPracticePhrasesSession, setShowPracticePhrasesSession] = useState(false);
  const [showPracticePicker, setShowPracticePicker] = useState(false);
  const [showPracticeCategorySession, setShowPracticeCategorySession] = useState(false);
  const [showContextualPractice, setShowContextualPractice] = useState(false);
  const [dragOverCategory, setDragOverCategory] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showGenerateStory, setShowGenerateStory] = useState(false);
  const [showArrangeWithAI, setShowArrangeWithAI] = useState(false);
  const [showAIFolderGenerator, setShowAIFolderGenerator] = useState(false);
  const [showAddText, setShowAddText] = useState(false);
  
  const derivedIsInstanceOwner = useMemo(() => {
    if (!selectedInstanceId || !user) return false;
    const found = instancesWithRoles.find(i => i.instance.id === selectedInstanceId);
    return found?.user_role === 'owner' || found?.instance.created_by === user.id;
  }, [selectedInstanceId, instancesWithRoles, user]);
  const isInstanceOwner = propIsInstanceOwner || derivedIsInstanceOwner;

  // Sync editName with category.name when category changes
  useEffect(() => {
    if (!isEditing) {
      setEditName(category.name);
    }
  }, [category.name, isEditing]);

  // Open category view when parent requests it (e.g. after AI-generated tree)
  useEffect(() => {
    if (
      openCategoryViewRequestId != null &&
      openCategoryViewRequestId === category.id &&
      onCategoryViewRequestHandled
    ) {
      setShowCategoryView(true);
      onCategoryViewRequestHandled();
    }
  }, [openCategoryViewRequestId, category.id, onCategoryViewRequestHandled]);

  const handleRename = async () => {
    if (!editName.trim()) {
      setEditName(category.name);
      setIsEditing(false);
      return;
    }

    if (editName.trim() === category.name) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await apiClient.updateCategory(category.id, { name: editName.trim() });
      onRename(category.id, editName.trim());
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error renaming category:', err);
      setEditName(category.name); // Reset on error
      alert('Failed to rename category. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(category.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRename();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Only save on blur if the user didn't click the save/cancel buttons
    // Check if the related target is one of our buttons
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && (relatedTarget.closest('.edit-actions') || relatedTarget.closest('button'))) {
      return; // Don't save if clicking a button
    }
    // Small delay to allow button clicks to register
    setTimeout(() => {
      if (isEditing) {
        handleRename();
      }
    }, 200);
  };

  const handleDelete = async () => {
    // Create a temporary TreeItem structure for the category to use with conditional delete
    const categoryAsTreeItem = {
      id: category.id,
      name: category.name,
      type: 'folder' as const,
      parent_id: null,
      is_folder: true,
      children: category.children,
      isExpanded: category.isExpanded,
      isCategory: true, // Add flag to identify this as a category
    };

    try {
      setIsDeleting(true);
      await handleConditionalDelete(
        categoryAsTreeItem,
        () => {
          // Category deletion should refresh the entire category list
          onCategoryUpdate();
        },
        (error) => {
          console.error('Error deleting category:', error);
          alert('Failed to delete category');
        }
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddChild = async (name: string) => {
    try {
      await apiClient.createCard({
        name,
        parent_id: null, // Root level items in category
        is_folder: addType === 'folder',
        category_id: category.id,
      });

      // Refresh only this specific category
      onCategoryRefresh(category.id);
      setShowAddInput(false);
    } catch (err: any) {
      console.error('Error adding child:', err);
      alert('Failed to add item');
    }
  };

  const handleAddChildToFolder = async (parentId: number, type: 'folder' | 'card', name: string) => {
    try {
      await apiClient.createCard({
        name,
        parent_id: parentId, // Set the folder as parent
        is_folder: type === 'folder',
        category_id: category.id, // Keep the same category
      });

      // Refresh the category to show the new card, and expand the parent folder
      onCategoryRefresh(category.id, parentId);
    } catch (err: any) {
      console.error('Error adding child to folder:', err);
      const errorMessage = err.message || err.response?.data?.detail || 'Failed to add item';
      alert(`Failed to add item: ${errorMessage}`);
    }
  };

  const handleAddFolder = () => {
    setAddType('folder');
    setShowAddInput(true);
  };

  const handleAddCard = () => {
    setAddType('card');
    setShowAddInput(true);
  };

  const handleAICardSuccess = (cardId: number) => {
    setShowAICardForm(false);
    setSelectedCardId(cardId);
    // Refresh the category to show the new card
    onCategoryRefresh(category.id);
  };

  const handleCardDetailClose = () => {
    setSelectedCardId(null);
  };

  const handleCardDetailSave = () => {
    // Refresh the category after saving
    onCategoryRefresh(category.id);
  };

  // Drag and drop handlers for category
  const handleCategoryDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategory(true);
  };

  const handleCategoryDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverCategory(false);
  };

  const handleCategoryDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCategory(false);
    
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const draggedItemId = dragData.id;
      
      // Move to this category
      await onMove(draggedItemId, { category_id: category.id, parent_id: null });
    } catch (err: any) {
      console.error('Error handling category drop:', err);
      alert(err.response?.data?.detail || 'Failed to move item');
    }
  };

  // Precompute leaf cards for Practice Phrases (uses tree data - works for members when getCardsHierarchy fails)
  const categoryLeafCards = useMemo(
    () =>
      flattenTree(category.children)
        .filter((item) => !item.is_folder)
        .map((item) => ({ id: item.id, name: item.name })),
    [category.children]
  );

  // Create dropdown menu items for category
  const getCategoryDropdownItems = (): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];

    if (SHOW_PRACTICE_PHRASES_IN_MENU && category.id > 0) {
      items.push({
        id: 'practice-phrases',
        label: 'Practice Phrases',
        icon: <Sparkles className="h-4 w-4" />,
        onClick: () => setShowPracticePhrasesSession(true),
      });
    }

    if (isInstanceOwner) {
      if (user?.email === 'tassiobs@gmail.com') {
        items.push({
          id: 'assign',
          label: 'Assign to students',
          icon: <UserPlus className="h-4 w-4" />,
          onClick: () => setShowAssignModal(true),
        });
      }
      items.push(
        createEditAction(() => setIsEditing(true)),
        {
          id: 'move-to-collection',
          label: 'Move to Collection',
          icon: <FolderInput className="h-4 w-4" />,
          onClick: () => setShowMoveModal(true),
        },
        createDeleteAction(handleDelete)
      );
    }

    return items;
  };

  // Calculate total items in category
  const totalItems = category.children.length;
  const folderCount = category.children.filter(child => child.is_folder).length;
  const cardCount = category.children.filter(child => !child.is_folder).length;

  return (
    <div className="border border-gray-200 rounded-xl mb-6 bg-white shadow-sm hover:shadow-md transition-shadow">
      {/* Category Header */}
      <div 
        className={`group flex flex-col lg:flex-row lg:items-center py-4 px-5 hover:bg-purple-50 rounded-t-xl border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50 ${
          dragOverCategory ? 'bg-green-100 border-green-400 border-2' : ''
        }`}
        onDragOver={handleCategoryDragOver}
        onDragLeave={handleCategoryDragLeave}
        onDrop={handleCategoryDrop}
      >
        {/* Row 1 (mobile) / Inline (desktop): Chevron, Icon, Name */}
        <div className="flex items-center flex-1 min-w-0 w-full">
          {/* Expand/collapse button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(category.id);
            }}
            className="mr-4 p-2 hover:bg-purple-100 rounded-lg transition-colors flex-shrink-0"
          >
            {category.isExpanded ? (
              <ChevronDown className="h-5 w-5 text-purple-600" />
            ) : (
              <ChevronRight className="h-5 w-5 text-purple-600" />
            )}
          </button>

          {/* Select-all checkbox — selection mode only */}
          {isSelectionMode && onSelectCard && categoryLeafCards.length > 0 && (() => {
            const allLeafIds = categoryLeafCards.map(c => c.id);
            const allSelected = allLeafIds.every(id => selectedCardIds.has(id));
            const someSelected = !allSelected && allLeafIds.some(id => selectedCardIds.has(id));
            return (
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={() => allLeafIds.forEach(id => onSelectCard(id, !allSelected))}
                onClick={e => e.stopPropagation()}
                className="mr-3 h-4 w-4 rounded border-gray-300 text-purple-600 cursor-pointer flex-shrink-0"
                title={allSelected ? 'Deselect all' : 'Select all'}
              />
            );
          })()}

          {/* Category Icon */}
          <div className="mr-4 flex-shrink-0">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Tag className="h-6 w-6 text-purple-600" />
            </div>
          </div>

          {/* Category Name and Stats */}
          <div 
            className="flex-1 min-w-0 cursor-pointer" 
            onClick={(e) => {
              if (!isEditing && (e.target as HTMLElement).closest('button') === null) {
                setShowCategoryView(true);
              }
            }}
          >
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  className="flex-1 border border-purple-300 rounded-md px-3 py-1 text-xl font-bold bg-white text-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                  disabled={isSaving}
                />
                <div className="edit-actions flex items-center space-x-1">
                  <button
                    onClick={handleRename}
                    disabled={isSaving || !editName.trim()}
                    className="p-1.5 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Save"
                  >
                    <Check className="h-5 w-5 text-green-600" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="p-1.5 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Cancel"
                  >
                    <X className="h-5 w-5 text-red-600" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h3 className="text-xl font-bold text-purple-900 break-words lg:truncate">
                  {category.name}
                </h3>
                {totalItems > 0 && (
                  <div className="flex items-center space-x-4 mt-1">
                    {/* Desktop stats - show text labels */}
                    <div className="hidden lg:flex items-center space-x-4">
                      <div className="flex items-center text-sm text-purple-600">
                        <Hash className="h-3 w-3 mr-1" />
                        <span>{totalItems} total</span>
                      </div>
                      {folderCount > 0 && (
                        <div className="flex items-center text-sm text-blue-600">
                          <Folder className="h-3 w-3 mr-1" />
                          <span>{folderCount} folders</span>
                        </div>
                      )}
                      {cardCount > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Hash className="h-3 w-3 mr-1" />
                          <span>{cardCount} cards</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Mobile stats - icon + number only */}
                    <div className="lg:hidden stats flex items-center space-x-3">
                      {folderCount > 0 && (
                        <div className="flex items-center bg-blue-50 px-2 py-1 rounded-md">
                          <Folder className="h-3 w-3 text-blue-600 mr-1" />
                          <span className="text-xs font-medium text-blue-700">{folderCount}</span>
                        </div>
                      )}
                      {cardCount > 0 && (
                        <div className="flex items-center bg-gray-50 px-2 py-1 rounded-md">
                          <FileText className="h-3 w-3 text-gray-600 mr-1" />
                          <span className="text-xs font-medium text-gray-700">{cardCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons + Dropdown */}
        <div className="flex items-center space-x-2 mt-3 lg:mt-0 lg:ml-3 flex-shrink-0 pl-20 lg:pl-0">
          {isInstanceOwner && (
            <>
              <button
                onClick={handleAddFolder}
                className="p-2 hover:bg-blue-100 rounded-lg bg-blue-50 transition-colors"
                title="Add folder"
              >
                <Folder className="h-4 w-4 text-blue-600" />
              </button>
              <button
                onClick={handleAddCard}
                className="p-2 hover:bg-gray-200 rounded-lg bg-gray-100 transition-colors"
                title="Add card"
              >
                <Plus className="h-4 w-4 text-gray-700" />
              </button>
            </>
          )}
          {category.id > 0 && (isInstanceOwner || categoryLeafCards.length > 0) && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowPracticePicker(true); }}
              title="Actions"
              className="p-2 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors"
            >
              <Zap className="h-4 w-4 text-amber-600" />
            </button>
          )}
          <DropdownMenu
            items={getCategoryDropdownItems()}
            className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
            disabled={isDeleting}
          />
        </div>
      </div>

      {/* Add item input */}
      {showAddInput && (
        <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
          <AddItemInput
            parentId={category.id}
            type={addType}
            onSubmit={handleAddChild}
            onCancel={() => setShowAddInput(false)}
            placeholder={`Add ${addType}...`}
          />
        </div>
      )}

      {/* Category Children */}
      {category.isExpanded && category.children.length > 0 && (
        <div className="p-5 bg-gray-50/30">
          <div className="space-y-1">
            {category.children.map((child) => (
              <TreeNode
                key={child.id}
                item={child}
                onToggle={onToggle}
                onRename={onRename}
                onDelete={onDelete}
                onAddChild={handleAddChildToFolder}
                onMove={onMove}
                onCategoryRefresh={onCategoryRefresh}
                level={0}
                categoryId={category.id}
                categories={categories}
                isInstanceOwner={isInstanceOwner}
                isSelectionMode={isSelectionMode}
                isSelected={selectedCardIds.has(child.id)}
                selectedCardIds={selectedCardIds}
                onSelectCard={onSelectCard}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {category.isExpanded && category.children.length === 0 && (
        <div className="p-12 text-center text-gray-500 bg-gray-50/30">
          <div className="p-4 bg-white rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <Tag className="h-8 w-8 text-purple-300" />
          </div>
          <p className="text-lg font-medium text-gray-600 mb-2">No items in this category yet</p>
          <p className="text-sm text-gray-500 mb-4">Add folders or cards to get started</p>
          {isInstanceOwner && (
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={handleAddFolder}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                Add Folder
              </button>
              <button
                onClick={handleAddCard}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Add Card
              </button>
            </div>
          )}
        </div>
      )}

      {/* AI Card Form Modal */}
      {showAICardForm && (
        <AICardForm
          categoryId={category.id}
          categoryName={category.name}
          onClose={() => setShowAICardForm(false)}
          onSuccess={handleAICardSuccess}
        />
      )}

      {/* Card Detail Modal */}
      {selectedCardId !== null && (
        <CardDetail
          cardId={selectedCardId}
          onClose={handleCardDetailClose}
          onSave={handleCardDetailSave}
        />
      )}

      {/* Unified Actions Picker */}
      {showPracticePicker && (
        <PracticePickerModal
          categoryName={category.name}
          onClose={() => setShowPracticePicker(false)}
          onFlashcards={() => setShowPracticeCategorySession(true)}
          onContextual={() => setShowContextualPractice(true)}
          onGenerateText={() => setShowGenerateStory(true)}
          hasCards={categoryLeafCards.length > 0}
          hasMultipleCards={categoryLeafCards.length > 1}
          isOwner={isInstanceOwner}
          onAICard={isInstanceOwner ? () => setShowAICardForm(true) : undefined}
          onAIFolder={isInstanceOwner ? () => setShowAIFolderGenerator(true) : undefined}
          onArrangeWithAI={isInstanceOwner ? () => setShowArrangeWithAI(true) : undefined}
          onAddText={isInstanceOwner ? () => setShowAddText(true) : undefined}
        />
      )}

      {/* Category Practice Session */}
      {showPracticeCategorySession && category.id > 0 && (
        <CategoryPracticeSession
          name={category.name}
          initialLeafCards={categoryLeafCards}
          onClose={() => setShowPracticeCategorySession(false)}
          isInstanceOwner={isInstanceOwner}
          onDelete={isInstanceOwner ? onDelete : undefined}
          onMove={isInstanceOwner ? onMove : undefined}
          categories={categories}
        />
      )}

      {/* Contextual Practice */}
      {showContextualPractice && category.id > 0 && (
        <ContextualPracticeModal
          categoryId={category.id}
          categoryName={category.name}
          leafCardCount={categoryLeafCards.length}
          onClose={() => setShowContextualPractice(false)}
        />
      )}

      {/* Category Practice Phrases Session */}
      {showPracticePhrasesSession && category.id > 0 && (
        <CategoryPracticePhrasesSession
          categoryId={category.id}
          categoryName={category.name}
          onClose={() => setShowPracticePhrasesSession(false)}
          initialLeafCards={categoryLeafCards}
        />
      )}

      {/* Category View Modal */}
      {showCategoryView && (
        <CategoryView
          category={category}
          onClose={() => setShowCategoryView(false)}
          onRename={onRename}
          onDelete={onDelete}
          onMove={onMove}
          onCategoryRefresh={onCategoryRefresh}
          categories={categories}
          isSelectionMode={isSelectionMode}
          selectedCardIds={selectedCardIds}
          onSelectCard={onSelectCard}
        />
      )}

      {/* Assign Category Modal */}
      {showAssignModal && selectedInstanceId && user && (
        <AssignFolderModal
          target={{ id: category.id, name: category.name }}
          targetType="category"
          instanceId={selectedInstanceId}
          currentUserId={user.id}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {}}
        />
      )}

      {/* Generate Story Modal */}
      {showGenerateStory && (
        <GenerateStoryModal
          categoryId={category.id}
          categoryName={category.name}
          onClose={() => setShowGenerateStory(false)}
        />
      )}

      {/* Add Text Modal */}
      {showAddText && (
        <AddTextModal
          categoryId={category.id}
          onClose={() => setShowAddText(false)}
          onSaved={() => onCategoryRefresh(category.id)}
        />
      )}

      {/* Arrange with AI Modal */}
      {showArrangeWithAI && (
        <ArrangeWithAIModal
          categoryId={category.id}
          categoryName={category.name}
          leafCards={categoryLeafCards}
          onClose={() => setShowArrangeWithAI(false)}
          onApplied={() => { setShowArrangeWithAI(false); onCategoryRefresh(category.id); }}
        />
      )}

      {/* Move to Collection Modal */}
      {showMoveModal && (
        <MoveToCollectionModal
          categoryId={category.id}
          currentInstanceId={selectedInstanceId}
          onSuccess={() => {
            setShowMoveModal(false);
            onCategoryUpdate();
          }}
          onClose={() => setShowMoveModal(false)}
        />
      )}

      {/* AI Folder Generator Modal */}
      {showAIFolderGenerator && category.id > 0 && (
        <AIFolderGenerator
          categoryId={category.id}
          parentId={null}
          userEmail={user?.email}
          onClose={() => setShowAIFolderGenerator(false)}
          onSuccess={(newFolderId) => {
            onCategoryRefresh(category.id, newFolderId);
          }}
        />
      )}
    </div>
  );
};
